/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { PidxPdsModalWrapperService } from '@myprofile-ui/pidx-ui-components'
import { MyProfileEmailWidgetListTranslations } from '../../interfaces/myprofile-email-widget.interfaces'
import { EmailCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/email/email.service'

@Component({
  selector: 'myprofile-email-list',
  templateUrl: './myprofile-email-list.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileEmailListComponent implements OnInit {
  private _pidxPdsModalWrapperService = inject(PidxPdsModalWrapperService)

  @Output() enterEditModeEvent = new EventEmitter<EmailCommunicationDto | null>()
  @Output() deleteItemEvent = new EventEmitter<EmailCommunicationDto>()

  @Input() translations!: MyProfileEmailWidgetListTranslations

  @Input() emailAddressBeingEdited: EmailCommunicationDto | null = null
  @Input() emails: EmailCommunicationDto[] = []
  @Input() isCnUser = false
  @Input() restrictGlobalUIChanges = false
  @Input() totalEmailCount = 0
  @Input() verifiedReplacementAvailable = false

  @Output() goToChangePorscheIdPageEvent = new EventEmitter()

  public anyEmailInThisListIsAPorscheId = false
  public anyEmailInThisListIsPending = false

  public ngOnInit(): void {
    this.anyEmailInThisListIsAPorscheId = this.emails.some((email) => email.isPorscheId)
    this.anyEmailInThisListIsPending = this.emails.some((email) => email.isPending)
  }

  public enterEditMode(item: EmailCommunicationDto | null) {
    this.enterEditModeEvent.emit(item)
  }

  public async deleteItem(item: EmailCommunicationDto) {
    if (!item.isPorscheId && !item.isStandard) {
      await this.showDeletePromptModal(item)
    } else if (!item.isStandard) {
      this.deleteItemEvent.emit(item)
    } else if (this.totalEmailCount <= 1 && !item.isVerified) {
      await this.showDeletePromptModal(item)
    } else if (this.verifiedReplacementAvailable) {
      await this.showDeletePromptModal(item)
    } else {
      const result = await this._pidxPdsModalWrapperService.openModal({
        title: this.translations.deleteStandardEmailHeadline,
        body: this.translations.deleteStandardEmailText,
        btnPrimary: this.translations.addButtonLabel,
        btnGhost: this.translations.cancelButtonLabel,
      })
      if (result) {
        this.enterEditMode(null)
        this.setFocusOnElementId('email-input')
      }
    }
  }

  public goToChangePorscheIdPage() {
    this.goToChangePorscheIdPageEvent.emit()
  }

  /**
   * Interpolate the email address into a translated aria-label template
   * (templates use the `{{EMAIL_ADDRESS}}` placeholder, e.g.
   * `"E-Mail-Adresse {{EMAIL_ADDRESS}} bearbeiten"`).
   */
  public a11yLabelFor(template: string | undefined, email: EmailCommunicationDto): string {
    return (template ?? '').replace('{{EMAIL_ADDRESS}}', email?.email ?? '')
  }

  /**
   * Show the module if you hit the delete btn
   */
  public async showDeletePromptModal(item: EmailCommunicationDto) {
    const title = this.translations.deleteConfirmationModalTitle
    const body = item.email
    const btnPrimary = this.translations.deleteConfirmationModalBtnPrimary
    const btnGhost = this.translations.deleteConfirmationModalBtnGhost
    const result = await this._pidxPdsModalWrapperService.openModal({ title, body, btnPrimary, btnGhost })
    if (result) {
      this.deleteItemEvent.emit(item)
    }
  }

  private setFocusOnElementId(id: string) {
    setTimeout(() => {
      const forFocus: HTMLElement | null = document.getElementById(id)
      if (forFocus) {
        forFocus.focus()
      }
    }, 500)
  }
}
